import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Badge as BadgeIcon,
  Cake as CakeIcon,
  Campaign as CampaignIcon,
  HomeOutlined as HomeOutlinedIcon,
  MailOutline as MailOutlineIcon,
  Notes as NotesIcon,
  PersonOutline as PersonOutlineIcon,
  PermIdentity as PermIdentityIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  TagOutlined as TagOutlinedIcon,
  WorkspacePremium as WorkspacePremiumIcon,
} from '@mui/icons-material';
import { CreateCustomerProfileDto } from '../../../api/models/CreateCustomerProfileDto';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid } from 'date-fns';
import * as S from '../../sales/orders/ExchangeModal/ExchangeDrawer.styles';
import type { ClientDrawerFormState } from './clientCreateDrawer.types';
import { drawerSectionCardSx, drawerSectionTitleSx } from './clientCreateDrawer.styles';
import { PhoneNumberInput } from '../../../components/ui';
import { toTitleCase } from '../../../utils/formatters/text';

export type ClientCreateDrawerFormStepProps = {
  form: ClientDrawerFormState;
  setForm: React.Dispatch<React.SetStateAction<ClientDrawerFormState>>;
  isSubmitting: boolean;
  nameError: string | null;
  emailError: string | null;
  identityDocumentError: string | null;
  phoneError: string | null;
  dateOfBirthAsDate: Date | null;
  minBirthDate: Date;
  maxBirthDate: Date;
  referenceBirthDate: Date;
};

const ClientCreateDrawerFormStep: React.FC<ClientCreateDrawerFormStepProps> = ({
  form,
  setForm,
  isSubmitting,
  nameError,
  emailError,
  identityDocumentError,
  phoneError,
  dateOfBirthAsDate,
  minBirthDate,
  maxBirthDate,
  referenceBirthDate,
}) => {
  const [showSecondReference, setShowSecondReference] = useState(
    () => form.referenceNumber2.trim() !== '',
  );

  useEffect(() => {
    if (form.referenceNumber2.trim()) {
      setShowSecondReference(true);
    }
  }, [form.referenceNumber2]);

  const handleRemoveSecondReference = () => {
    setForm((f) => ({ ...f, referenceNumber2: '' }));
    setShowSecondReference(false);
  };

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', width: '100%' }}>
      <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
        <S.SectionHeader>
          <BadgeIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
            Contacto principal
          </Typography>
        </S.SectionHeader>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Nombre completo"
                value={form.customerName}
                onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                onBlur={(e) =>
                  setForm((f) => ({ ...f, customerName: toTitleCase(e.target.value) }))
                }
                error={Boolean(nameError)}
                helperText={
                  nameError ??
                  'Obligatorio. Puede repetirse entre clientes; la CI es el identificador único.'
                }
                FormHelperTextProps={{
                  sx: nameError ? undefined : { color: 'text.secondary' },
                }}
                disabled={isSubmitting}
                size="small"
                placeholder="Ej. Maria Fernanda Rojas"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="CI (cédula de identidad)"
                value={form.identityDocumentNumber}
                onChange={(e) => setForm((f) => ({ ...f, identityDocumentNumber: e.target.value }))}
                error={Boolean(identityDocumentError)}
                helperText={
                  identityDocumentError ?? 'Opcional. Si se ingresa, debe ser única por cliente.'
                }
                FormHelperTextProps={{
                  sx: identityDocumentError ? undefined : { color: 'text.secondary' },
                }}
                disabled={isSubmitting}
                size="small"
                placeholder="Ej. 1234567"
                inputProps={{ maxLength: 32 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PermIdentityIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}
                >
                  Teléfono
                </Typography>
                <PhoneNumberInput
                  value={form.customerPhone}
                  onChange={(value) => setForm((f) => ({ ...f, customerPhone: value }))}
                  disabled={isSubmitting}
                  size="small"
                  nationalNumberLabel="Número"
                  nationalNumberPlaceholder="Ej. 71234567"
                  textFieldProps={{
                    error: Boolean(phoneError),
                    helperText: phoneError ?? 'Opcional. Código de país y número.',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Correo electrónico"
                type="email"
                value={form.customerEmail}
                onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
                error={Boolean(emailError)}
                helperText={emailError}
                disabled={isSubmitting}
                size="small"
                placeholder="cliente@correo.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                value={form.customerAddress}
                onChange={(e) => setForm((f) => ({ ...f, customerAddress: e.target.value }))}
                onBlur={(e) => setForm((f) => ({ ...f, customerAddress: e.target.value.trim() }))}
                disabled={isSubmitting}
                size="small"
                multiline
                minRows={2}
                placeholder="Calle, distrito, ciudad"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeOutlinedIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Nº de referencia"
                  value={form.referenceNumber1}
                  onChange={(e) => setForm((f) => ({ ...f, referenceNumber1: e.target.value }))}
                  disabled={isSubmitting}
                  size="small"
                  placeholder="Ej. código interno"
                  inputProps={{ maxLength: 50 }}
                  helperText={showSecondReference ? undefined : 'Opcional. Máximo 2 por cliente.'}
                  FormHelperTextProps={{ sx: { color: 'text.secondary' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagOutlinedIcon fontSize="small" sx={{ color: '#6366f1' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {!showSecondReference ? (
                  <Tooltip title="Agregar segunda referencia">
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => setShowSecondReference(true)}
                        disabled={isSubmitting}
                        aria-label="Agregar segunda referencia"
                        sx={(theme) => ({
                          mt: 0.5,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                          borderRadius: 2,
                        })}
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            </Grid>
            {showSecondReference ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Segunda referencia"
                    value={form.referenceNumber2}
                    onChange={(e) => setForm((f) => ({ ...f, referenceNumber2: e.target.value }))}
                    disabled={isSubmitting}
                    size="small"
                    placeholder="Ej. código alterno"
                    inputProps={{ maxLength: 50 }}
                    helperText="Opcional"
                    FormHelperTextProps={{ sx: { color: 'text.secondary' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TagOutlinedIcon fontSize="small" sx={{ color: '#6366f1' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Quitar segunda referencia">
                    <span>
                      <IconButton
                        onClick={handleRemoveSecondReference}
                        disabled={isSubmitting}
                        aria-label="Quitar segunda referencia"
                        sx={(theme) => ({
                          mt: 0.5,
                          color: theme.palette.text.secondary,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          '&:hover': {
                            color: theme.palette.error.main,
                            borderColor: alpha(theme.palette.error.main, 0.5),
                          },
                        })}
                      >
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </S.SectionBox>

      <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
        <S.SectionHeader>
          <WorkspacePremiumIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
            Perfil y preferencias
          </Typography>
        </S.SectionHeader>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Fecha de nacimiento"
                format="dd/MM/yyyy"
                value={dateOfBirthAsDate}
                onChange={(newDate) => {
                  setForm((f) => ({
                    ...f,
                    dateOfBirth: newDate && isValid(newDate) ? format(newDate, 'yyyy-MM-dd') : '',
                  }));
                }}
                views={['year', 'month', 'day']}
                openTo="year"
                yearsPerRow={4}
                minDate={minBirthDate}
                maxDate={maxBirthDate}
                referenceDate={referenceBirthDate}
                disabled={isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CakeIcon fontSize="small" sx={{ color: '#6366f1' }} />
                        </InputAdornment>
                      ),
                    },
                  },
                  field: { clearable: true },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={isSubmitting}>
                <InputLabel id="client-gender-label">Género</InputLabel>
                <Select
                  labelId="client-gender-label"
                  label="Género"
                  value={form.gender}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      gender: e.target.value as ClientDrawerFormState['gender'],
                    }))
                  }
                >
                  <MenuItem value="">Sin especificar</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.gender.MALE}>Masculino</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.gender.FEMALE}>Femenino</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.gender.OTHER}>Otro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" disabled={isSubmitting}>
                <InputLabel id="client-tier-label">Nivel de fidelidad</InputLabel>
                <Select
                  labelId="client-tier-label"
                  label="Nivel de fidelidad"
                  value={form.loyaltyTier}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      loyaltyTier: e.target.value as CreateCustomerProfileDto.loyaltyTier,
                    }))
                  }
                >
                  <MenuItem value={CreateCustomerProfileDto.loyaltyTier.BRONZE}>Bronce</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.loyaltyTier.SILVER}>Plata</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.loyaltyTier.GOLD}>Oro</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.loyaltyTier.PLATINUM}>Platino</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" disabled={isSubmitting}>
                <InputLabel id="client-payment-label">Método de pago preferido</InputLabel>
                <Select
                  labelId="client-payment-label"
                  label="Método de pago preferido"
                  value={form.preferredPaymentMethod}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      preferredPaymentMethod: e.target
                        .value as ClientDrawerFormState['preferredPaymentMethod'],
                    }))
                  }
                >
                  <MenuItem value="">Sin especificar</MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.preferredPaymentMethod.CASH}>
                    Efectivo
                  </MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.preferredPaymentMethod.CARD}>
                    Tarjeta
                  </MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.preferredPaymentMethod.BANK_TRANSFER}>
                    Transferencia
                  </MenuItem>
                  <MenuItem value={CreateCustomerProfileDto.preferredPaymentMethod.QR_CODE}>
                    Código QR
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Origen / referido"
                value={form.referralSource}
                onChange={(e) => setForm((f) => ({ ...f, referralSource: e.target.value }))}
                disabled={isSubmitting}
                size="small"
                placeholder="Redes sociales, recomendación, tienda..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CampaignIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </S.SectionBox>

      <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
        <S.SectionHeader>
          <NotesIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
            Observaciones
          </Typography>
        </S.SectionHeader>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                value={form.customerNotes}
                onChange={(e) => setForm((f) => ({ ...f, customerNotes: e.target.value }))}
                disabled={isSubmitting}
                size="small"
                multiline
                minRows={4}
                placeholder="Preferencias, contexto de atención, comentarios importantes..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NotesIcon fontSize="small" sx={{ color: '#6366f1' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={(theme) => ({
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.06)
                      : theme.palette.grey[50],
                })}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.marketingConsent}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, marketingConsent: e.target.checked }))
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label="Acepta comunicaciones de marketing"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </S.SectionBox>
    </Box>
  );
};

export default ClientCreateDrawerFormStep;
